"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import {
  useState, useEffect, useMemo, useContext, createContext,
} from "react";
import {
  dayjsifyPlanning, spliceSlots,
} from "@audacia-hq/shared/utils";
import { Subject } from "rxjs";

import {
  Expert, ExpertDataMap, ExpertIdentifierMap, ExpertStatusMap,
} from "../models/expert";
import { getExpertsData } from "../services/api";

import { useWS, WSMessage } from "./WSContext";

interface ExpertContextType {
  unfilteredExpertIdentifiers: ExpertIdentifierMap;
  expertIdentifiers: ExpertIdentifierMap;
  getExperts: (expertUids: string[]) => Promise<Expert[]>;
  expertUpdateFeed: Subject<string[]>
  expertsDataLoading: boolean;
}

const ExpertContext = createContext<ExpertContextType>({
  unfilteredExpertIdentifiers: {},
  expertIdentifiers: {},
  getExperts: (e) => Promise.resolve([]),
  expertUpdateFeed: new Subject(),
  expertsDataLoading: true,
});

interface Props {
  children: React.ReactNode;
  /** Unfiltered expert identifiers */
  initialExpertIdentifiers: ExpertIdentifierMap;
}

const ExpertProvider: React.FC<Props> = ({
  children, initialExpertIdentifiers,
}) => {
  const [unfilteredExpertIdentifiers, setUnfilteredExpertIdentifiers] = useState<ExpertIdentifierMap>(initialExpertIdentifiers);
  const expertIdentifiers = useMemo(() => Object.values(unfilteredExpertIdentifiers).reduce<ExpertIdentifierMap>(
    (aggr, identifier) => (identifier.status !== "ACTIVATED" ? aggr : { ...aggr, [identifier.expertUid]: identifier }),
    {},
  ), [unfilteredExpertIdentifiers]);
  const [expertsData, setExpertsData] = useState<ExpertDataMap>({});
  const [expertsStatus, setExpertsStatus] = useState<ExpertStatusMap>({});
  const [expertChanges, setExpertChanges] = useState<string[]>([]);
  const [expertUpdateFeed] = useState<Subject<string[]>>(new Subject<string[]>());
  const [dataLoading, setDataLoading] = useState<boolean>(false);

  const ws = useWS();

  const collectExpertsData = (toCollect: Expert[]) => toCollect
    .reduce<{ data: ExpertDataMap, status: ExpertStatusMap }>((res, expert) => {
      const {
        presenceStatus, queue, planning, ...expertData
      } = expert;
      return {
        data: {
          ...res.data,
          [expert.expertUid]: expertData,
        },
        status: {
          ...res.status,
          [expert.expertUid]: {
            presenceStatus,
            queue,
            planning: planning ? dayjsifyPlanning(planning as any) as Expert["planning"] : undefined,
          },
        },
      };
    }, { data: expertsData, status: expertsStatus });

  const getExperts = (expertUids: string[]) => {
    const toFetch = expertUids.filter((eUid) => !expertsData[eUid]);
    if (toFetch.length > 0) {
      setDataLoading(true);
      return getExpertsData(toFetch).then(({ data: d }) => {
        const { data: newExpertsData, status: newExpertStatus } = collectExpertsData(d.expertsData);
        setExpertsData((prev) => ({ ...prev, ...newExpertsData }));
        setExpertsStatus((prev) => ({ ...prev, ...newExpertStatus }));
        return expertUids.map((eUid) => ({ ...newExpertsData[eUid], ...newExpertStatus[eUid] }));
      })
        .finally(() => setDataLoading(false));
    }
    return Promise.resolve(expertUids.map((eUid) => ({ ...expertsData[eUid], ...expertsStatus[eUid] })));
  };

  const handleExpertsEvents = (msg: WSMessage, expertsData: ExpertDataMap) => {
    switch (msg.event) {
      case "ws.reconnected":
        getExperts(Object.keys(expertsData)); // should reload expert data
        break;
      case "expert.waitingRoom.updated":
        handleWaitingRoomUpdate(msg);
        break;
      case "expert.planning.updated":
        handlePlanningUpdate(msg);
        break;
      case "expert.status.updated":
        handleStatusUpdate(msg);
        break;
      case "expert.status.updatedBatch":
        handleStatusBatchUpdate(msg);
        break;
      default:
        break;
    }
  };

  const handleWaitingRoomUpdate = (msg: WSMessage) => {
    const payload = JSON.parse(msg.payload);
    setExpertsStatus((prev) => {
      if (!prev[msg.subject]) return prev; // checking existence inside setState to avoid useCallback on handleExpertEvent
      const newExpert = {
        ...prev[msg.subject],
        queue: {
          ...prev[msg.subject].queue,
          items: payload.items,
        },
      };
      setExpertChanges(() => [msg.subject]);
      return { ...prev, [msg.subject]: newExpert };
    });
  };

  const handlePlanningUpdate = (msg: WSMessage) => {
    const payload = JSON.parse(msg.payload);
    const planning = dayjsifyPlanning(payload);
    setExpertsStatus((prev) => {
      if (!prev[msg.subject]) return prev;
      const spliced = splicePlanning(planning, prev[msg.subject].planning);
      const newExpert = {
        ...prev[msg.subject],
        planning: spliced,
      };
      setExpertChanges(() => [msg.subject]);
      return { ...prev, [msg.subject]: newExpert };
    });
  };

  const handleStatusUpdate = (msg: WSMessage) => {
    const { activityStatus, availability, writtenStatus } = JSON.parse(msg.payload);
    if (availability?.phoneStatus || availability?.videoStatus || availability?.livechatStatus) {
      setUnfilteredExpertIdentifiers((prev) => {
        if (!prev[msg.subject]) return prev;
        return {
          ...prev,
          [msg.subject]: {
            ...prev[msg.subject],
            online: true,
          },
        };
      });
    }
    setExpertsStatus((prev) => {
      if (!prev[msg.subject]) return prev;
      const newExpert = {
        ...prev[msg.subject],
        presenceStatus: {
          ...prev[msg.subject].presenceStatus,
          ...(activityStatus !== undefined ? { activityStatus } : {}),
          ...(availability || {}),
          ...(writtenStatus !== undefined ? { writtenStatus } : {}),
        },
      };
      setExpertChanges(() => [msg.subject]);
      return { ...prev, [msg.subject]: newExpert };
    });
  };

  const handleStatusBatchUpdate = (msg: WSMessage) => {
    const batch = JSON.parse(msg.payload);
    setUnfilteredExpertIdentifiers((prev) => batch.reduce((aggr, { expertUid, availability }) => {
      if (!aggr[expertUid]) return aggr;
      return {
        ...aggr,
        [expertUid]: {
          ...aggr[expertUid],
          online: availability?.phoneStatus || availability?.videoStatus || availability?.livechatStatus,
        },
      };
    }, prev));
    setExpertsStatus((prev) => {
      const { state, changes } = batch.reduce((aggr, { expertUid, availability }) => {
        if (!aggr.state[expertUid]) return aggr;
        return {
          state: {
            ...aggr.state,
            [expertUid]: {
              ...aggr.state[expertUid],
              presenceStatus: {
                ...aggr.state[expertUid].presenceStatus,
                ...availability,
              },
            },
          },
          changes: [...aggr.changes, expertUid],
        };
      }, { state: prev, changes: [] });
      setExpertChanges(() => changes);
      return state;
    });
  };

  useEffect(() => {
    const sub = ws.subscribe((msg: WSMessage) => handleExpertsEvents(msg, expertsData));
    return () => sub.unsubscribe();
  }, [ws, expertsData]);

  const splicePlanning = (newPlanningRange, oldPlanning) => ({
    ...oldPlanning,
    slots: spliceSlots(oldPlanning?.slots || [], newPlanningRange.slots, newPlanningRange.from, newPlanningRange.to),
    queueSlots: spliceSlots(oldPlanning?.queueSlots || [], newPlanningRange.queueSlots, newPlanningRange.from, newPlanningRange.to),
  });

  useEffect(() => {
    if (expertChanges.length === 0) return;
    expertUpdateFeed.next(expertChanges);
  }, [expertChanges]);

  const value = useMemo(
    () => (
      {
        unfilteredExpertIdentifiers,
        expertIdentifiers,
        getExperts,
        expertUpdateFeed,
        expertsDataLoading: dataLoading,
      }),
    [unfilteredExpertIdentifiers, expertIdentifiers, getExperts, expertUpdateFeed, dataLoading],
  );

  return (
    <ExpertContext.Provider value={value}>
      {children}
    </ExpertContext.Provider>
  );
};

const useExpert = () => useContext(ExpertContext);

export default ExpertContext;
export {
  ExpertProvider, useExpert,
};
